// Self-contained validator for a Stellar ed25519 public key ("G..." address).
//
// Replaces `StrKey.isValidEd25519PublicKey` from `@stellar/stellar-base`. That package is
// CommonJS-only with no tree-shaking (its `browser` field maps the entry to a pre-bundled
// minified lib), so importing one static method dragged the entire crypto library — Keypair,
// xdr, Operation, TransactionBuilder, @noble/curves, js-xdr, bignumber.js — into the common
// dashboard chunk where user-identifier.ts lives. This validator has zero dependencies.
//
// A canonical ed25519 public-key strkey is: version byte (6 << 3) + 32-byte key + 2-byte
// CRC16-XMODEM checksum, RFC4648-base32-encoded to exactly 56 chars. 56 * 5 = 280 bits = 35
// bytes exactly (no leftover bits), so decoding is bijective and the "canonical re-encoding"
// check that guards partial-byte base32 cases is unnecessary here.

const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
const STRKEY_LENGTH = 56;
const DECODED_LENGTH = 35; // 1 version byte + 32 key bytes + 2 checksum bytes
const ED25519_PUBLIC_KEY_VERSION_BYTE = 6 << 3; // 48 — the "G" prefix
const CONTRACT_VERSION_BYTE = 2 << 3; // 16 — the "C" prefix (Soroban contracts, incl. SACs)

// Reverse lookup: base32 char -> 5-bit value (-1 for anything outside the alphabet).
const CHAR_TO_VALUE: Record<string, number> = {};
for (let i = 0; i < BASE32_ALPHABET.length; i += 1) {
  CHAR_TO_VALUE[BASE32_ALPHABET[i]] = i;
}

// CRC16-XMODEM table (polynomial 0x1021, MSB-first, init 0x0000). Built once at module load;
// equivalent to the literal table shipped by stellar-base's calculateChecksum.
const CRC_TABLE = (() => {
  const table = new Uint16Array(256);
  for (let i = 0; i < 256; i += 1) {
    let crc = i << 8;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = (crc & 0x8000 ? (crc << 1) ^ 0x1021 : crc << 1) & 0xffff;
    }
    table[i] = crc;
  }
  return table;
})();

function crc16Xmodem(bytes: number[]): number {
  let crc = 0;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = ((crc << 8) ^ CRC_TABLE[((crc >> 8) ^ bytes[i]) & 0xff]) & 0xffff;
  }
  return crc;
}

/**
 * Shared decode + checksum check. `expectedVersionByte` is what distinguishes an account key
 * from a contract key — the rest of the encoding is identical for both.
 */
function isValidStrKey(address: string, expectedVersionByte: number): boolean {
  if (typeof address !== 'string' || address.length !== STRKEY_LENGTH) {
    return false;
  }

  // Strict base32 decode into exactly 35 bytes.
  const decoded: number[] = [];
  let buffer = 0;
  let bitsInBuffer = 0;
  for (let i = 0; i < address.length; i += 1) {
    const value = CHAR_TO_VALUE[address[i]];
    if (value === undefined) {
      return false; // char outside the RFC4648 alphabet (rejects lowercase, "0x…", padding, etc.)
    }
    buffer = (buffer << 5) | value;
    bitsInBuffer += 5;
    if (bitsInBuffer >= 8) {
      bitsInBuffer -= 8;
      decoded.push((buffer >> bitsInBuffer) & 0xff);
    }
  }

  if (decoded.length !== DECODED_LENGTH || decoded[0] !== expectedVersionByte) {
    return false;
  }

  const payload = decoded.slice(0, DECODED_LENGTH - 2); // version byte + 32-byte key
  const crc = crc16Xmodem(payload);
  // Checksum is stored little-endian in the trailing two bytes.
  return (crc & 0xff) === decoded[DECODED_LENGTH - 2] && ((crc >> 8) & 0xff) === decoded[DECODED_LENGTH - 1];
}

/**
 * Returns true if `address` is a canonical, checksum-valid Stellar ed25519 public key.
 * Case-sensitive (base32 is uppercase); mirrors `StrKey.isValidEd25519PublicKey`.
 */
export function isValidStellarEd25519PublicKey(address: string): boolean {
  return isValidStrKey(address, ED25519_PUBLIC_KEY_VERSION_BYTE);
}

/**
 * Returns true if `address` is a canonical, checksum-valid Stellar contract address ("C...").
 * Mirrors `StrKey.isValidContract`. Rejects account ("G"), muxed ("M") and secret seed ("S")
 * keys, which share the encoding but carry a different version byte.
 */
export function isValidStellarContract(address: string): boolean {
  return isValidStrKey(address, CONTRACT_VERSION_BYTE);
}
